import React, { useState, useContext, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AxiosInstance, { AxiosError, ErrorResponseData, AuthenticationType, errorMessageForCode } from "../../helpers/axiosInstance";
import { Link } from 'react-router-dom';
import { Button, Form, Grid, Message, Image } from 'semantic-ui-react';
import Swal from "sweetalert2";
import imageLoginTopCurve from "../../assets/images/login-top-curve.svg";
import imageLoginLeftCurve from "../../assets/images/login-left-curve.svg";
import imageLoginMicrosoftLogo from "../../assets/images/login-microsoft-logo.svg";
import imageLoginLeft from "../../assets/images/login-left.svg";
import AppContext, { AuthTokens, token_config } from '../../AppContext';
import { AzureSignIn } from '../../helpers/azureAuth';

interface FormErrors {
  username?: string;
  password?: string;
  serverError?: string; // Add this line
}

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const jwtToken = searchParams.get('token');
  const redirectUrl: any = searchParams.get('redirectUrl');
  
  const formRef = useRef<HTMLFormElement | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const appContext = useContext(AppContext);
  const { setAuthTokens } = appContext;

  const [loginModel, setLoginModel] = useState({
    idToken: null,
    identityProvider: 1,
    email: null,
    password: null,
  });

  useEffect(() => {
    const setAccessToken = async () => {
      if (jwtToken) {
        await localStorage.setItem(token_config.ACCESS_TOKEN, jwtToken);
        localStorage.removeItem('dashboardFilter');
        window.location.href = redirectUrl ? redirectUrl : '/dashboard';
      }
    }
    setAccessToken();
  }, [jwtToken, redirectUrl]);

  useEffect(() => {
    const fetchLoginModel = async () => {
      try {
        const response = await AxiosInstance.get("/Auth/GetLoginRequest");
        setLoginModel(response.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchLoginModel();
  }, []);

  function isErrorResponseData(data: any): data is ErrorResponseData {
    return typeof data === "object" && "ErrorCode" in data && "Message" in data;
  }

  const validate = () => {
    const newErrors: FormErrors = {};

    if (!username) {
      newErrors.username = "Username is required";
    }

    if (!password) {
      newErrors.password = "Password is required";
    }

    return newErrors;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent the default Enter key behavior
      handleFormSubmit(); // Programmatically submit the form
    }
  };


  const handleAzureLogin = async () => {
    try {
      setIsLoading(true);
      const { idToken, error } = await AzureSignIn();

      if (idToken) {
        // Redirect the user to the main page or handle successful login in another way

        const response = await AxiosInstance.post("/Auth/Login", {
          ...loginModel,
          authenticationTypeId: AuthenticationType.Azure, // Use the enum value here
          idToken: idToken,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        const responseAuthTokens = {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        } as AuthTokens;

        setAuthTokens(responseAuthTokens);
        localStorage.removeItem('dashboardFilter');
        window.location.href = '/dashboard';

      } else {
        // Handle the error, for example, by showing an error message or retrying the login process
        console.error('Login error:', error);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response && isErrorResponseData(axiosError.response.data)) {
        const errorMessage = axiosError.response.data.Message;
        Swal.fire({
          icon: 'error',
          title: 'Ope!',
          html: `<p>${errorMessage}</p><p>(401 unauthorized)</p>`,
          confirmButtonText: 'OK',
        }).then(() => {
          window.location.href = '/';
        });
      }
    } finally {
      setIsLoading(false);
    }
  };


  const handleFormSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsLoading(true);
    try {
      const response = await AxiosInstance.post("/Auth/Login", {
        ...loginModel,
        authenticationTypeId: AuthenticationType.Form,
        email: username,
        password,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      const responseAuthTokens = {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      } as AuthTokens;

      setAuthTokens(responseAuthTokens);
      localStorage.removeItem('dashboardFilter');
      window.location.href = '/dashboard';
    } catch (error) { 
      const axiosError = error as AxiosError;
      if (axiosError.response && isErrorResponseData(axiosError.response.data)) {
        const errorCode = axiosError.response.data.ErrorCode;
        const errorMessage = axiosError.response.data.Message;
        if (errorCode === "credentials_invalid") {
          setErrors({ ...errors, serverError: errorMessage });
        } else if (errorCode === "account_locked") {
          Swal.fire({
            icon: 'error',
            title: 'Ope!',
            html: `<p>${errorMessage}</p><p>(401 unauthorized)</p>`,
            confirmButtonText: 'OK',
          }).then(() => {
            window.location.href = '/';
          });
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Ope!',
          html: `<p>${axiosError.message}</p><p>(${errorMessageForCode(axiosError?.response?.status)})</p>`,
          confirmButtonText: 'OK',
        })
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Grid>
        <Grid.Row only='computer' style={{
          padding: 0
        }}>
          <Grid.Column>
            <Image src={imageLoginTopCurve} style={{
              position: 'absolute',
              margin: '0 auto',
              left: '35%'
            }} />
            <Image src={imageLoginLeftCurve} style={{
              position: 'absolute',
              left: '-25px',
              top: '-10px'
            }} />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Grid>
        <Grid.Row>
          <Grid.Column className='computer-only' computer={9} style={{
            top: '15%'
          }}>
            <div className="Adjure-Logo" />
            <Image src={imageLoginLeft} style={{
              width: '85%',
              height: 'auto'
            }} />
          </Grid.Column>
          <Grid.Column mobile={12} tablet={12} computer={6} style={{
            top: '10%',
            margin: '0 auto'
          }}>
            <div className="Adjure-Logo mobile-only" />
            <div className="innerDiv">
              <h2>Sign in</h2>
              <div className='microsoft'>
                <Button style={{ margin: '0 auto' }} onClick={handleAzureLogin} loading={isLoading}>
                  <Image src={imageLoginMicrosoftLogo} style={{ marginRight: '10px', display: 'inline-block' }} />
                  <span className="Sign-in-with-Microsoft" >
                    Sign in with Microsoft
                  </span>
                </Button>
              </div>
              <div className="ui horizontal divider">
                Or with email
              </div>
              <Form onSubmit={handleFormSubmit} error={!!errors.username || !!errors.password || !!errors.serverError} ref={formRef}>
                <Form.Field>
                  <Form.Input
                    placeholder="Email Address"
                    type="text"
                    id="username"
                    error={!!errors.username}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  {errors.username && <div className="error-message">{errors.username}</div>}
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    placeholder="Password"
                    type="password"
                    error={!!errors.password}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className='textbox'
                    onKeyDown={handleKeyDown}
                  />
                  {errors.password && <div className="error-message">{errors.password}</div>}
                </Form.Field>
                <Form.Field style={{ fontSize: '16px' }}>
                  {errors.serverError && <Message error content={errors.serverError} />}
                </Form.Field>
                <div className='Forgot-Password'><Link to="/forgot_password">Forgot Password?</Link></div>
                <Button className='signin' type="submit" loading={isLoading}>
                  <span className="signintext">Sign in</span>
                </Button>
              </Form>
            </div>
            <div className='mt-2' style={{ float: 'right' }}>Questions? <a href='mailto:AdjureSupport@minneapolismn.gov?subject=Adjure CRM login help' style={{ color: '#6f52f7' }}>Contact Adjure Support</a></div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

export default LoginPage;
